import React, { useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter, Select, SelectOption } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { CatalogueEmptyReason } from '../../graphql/__generated__/schema';
import {
    AssignableDashboardsDocument,
    GrantDashboardDocument,
    PortalUserDocument,
} from '../../graphql/__generated__/types';
import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from '../accounts/portalAccess';
import { dashboardRefusalOf } from './dashboardRefusal';
import { blockedNotice } from './dashboards';

interface Props {
    /** The gesture's subject: the **membership**, never the person. */
    membershipId: string;
    organizationId: string;
    organizationName: string;
    personName: string;
    /** What this membership already holds, by catalogue entry. */
    heldDashboardIds: string[];
    /** Said by the sheet when the access is not active, or null. See `dashboards.ts`. */
    inactiveAccess: string | null;
    onClose: () => void;
}

/**
 * Granting a dashboard to **one membership**: PCG-106.
 *
 * **Attention, the dialog is opened from an account block and takes that account's
 * membership, which is what makes it correct.** An habilitation hangs from a
 * membership: the entry granted must be the object of that membership's account, and
 * the API refuses anything else with `DASHBOARD_NOT_ON_ACCOUNT`. So the catalogue is
 * read **narrowed to this one account**: not to offer less, but because an entry of
 * a sibling account would compose a gesture that can only be refused. Two accounts
 * may carry an entry with the same label, and the label is not what tells them apart.
 *
 * **Attention, what the payload is for.** `grantDashboard` refuses neither a closed module nor
 * a missing perimeter parameter: configuring before opening is the normal order of
 * the two gestures, so a grant may legitimately produce an habilitation that opens
 * nothing. The mutation answers with what the membership holds afterwards, read and
 * not deduced, so the confirmation says which of the two just happened instead of
 * congratulating the operator on an access nobody can use.
 *
 * **Attention, the refused entries stay in the list, disabled with their qualifier.** A picker
 * offering one entry out of four reads as a catalogue with one entry, and sends the
 * operator declaring a dashboard that already exists.
 */
const GrantDashboardDialog = ({
    membershipId,
    organizationId,
    organizationName,
    personName,
    heldDashboardIds,
    inactiveAccess,
    onClose,
}: Props) => {
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const {
        data,
        loading: catalogueLoading,
        error: catalogueError,
    } = useQuery(AssignableDashboardsDocument, { variables: { organizationIds: [organizationId] } });

    const [grant, { data: granted, loading, error }] = useMutation(GrantDashboardDocument, {
        // The sheet lists the habilitations of every membership, and this writes one.
        // The membership objects carry no `id`, so Apollo normalises none of them and
        // the payload cannot be written into the sheet's cache entry: refetching is
        // the honest way rather than a clever one.
        refetchQueries: [PortalUserDocument],
        awaitRefetchQueries: true,
    });

    const entries = (data?.assignableDashboards?.dashboards ?? []).filter(Boolean);
    const held = new Set(heldDashboardIds);

    /** Why this entry cannot be granted, or null when it can. Kept to the qualifier. */
    const entryBlocker = (entry: { id?: string | null; perimeterLocked?: boolean | null }): string | null => {
        if (entry.id && held.has(entry.id)) {
            return c('Grant dashboard').t`Already granted`;
        }
        // Attention, the API tolerates it: `grantDashboard` refuses only an archived entry,
        // but the two other ways of granting a dashboard refuse it, and this third one
        // must not be the loophole: the habilitation would be signed for nobody and
        // open nothing. The catalogue is where that is repaired.
        if (entry.perimeterLocked !== true) {
            return c('Grant dashboard').t`No perimeter parameter`;
        }
        return null;
    };

    const options: SelectOption<string>[] = entries
        .filter((entry) => entry.id)
        .map((entry) => {
            const blocker = entryBlocker(entry);
            return {
                label: entry.name ?? '',
                value: entry.id as string,
                disabled: Boolean(blocker),
                disabledReason: blocker ?? undefined,
            };
        });

    /**
     * **Attention, A choice with a single option is stated, never offered.** One entry in the
     * catalogue is the common case on an account that has just been set up, and a
     * one-row picker is a control the operator opens for nothing. The entry is written
     * out instead, including when it cannot be granted, which is why the qualifier
     * follows it rather than disappearing with the list.
     */
    const single = entries.length === 1 ? entries[0] : null;
    const chosenId = single?.id ?? selectedId;
    const chosen = entries.find((entry) => entry.id === chosenId) ?? null;
    const chosenBlocker = chosen ? entryBlocker(chosen) : null;

    /** Why the catalogue could not be read. Not a refusal of the gesture: nothing was attempted. */
    const readRefusal = (): Refusal => {
        switch (refusalCode(catalogueError)) {
            case 'MISSING_PERMISSION':
                return {
                    title: c('Grant dashboard').t`This account's catalogue could not be read.`,
                    guidance: c('Grant dashboard')
                        .t`It is read under the user-management permission, the same one that grants a dashboard, and your role does not carry it. The list was refused, not answered: it is not empty, it was not read.`,
                };
            case 'FORBIDDEN_OUT_OF_SCOPE':
                return {
                    title: c('Grant dashboard').t`This account is outside your portfolio.`,
                    guidance: c('Grant dashboard')
                        .t`Its catalogue was refused rather than answered, and granting would be refused the same way. Ask for this account to be added to your portfolio.`,
                };
            default:
                return {
                    title: c('Grant dashboard').t`This account's catalogue could not be read.`,
                    guidance: c('Grant dashboard')
                        .t`Nothing is offered below for want of an answer, not for want of entries. Nothing was granted.`,
                    detail: catalogueError instanceof Error ? catalogueError.message : String(catalogueError),
                };
        }
    };

    /** Why the catalogue has nothing to offer, or null when it has. */
    const emptiness = (): { title: string; guidance: string } | null => {
        if (catalogueError || entries.length > 0) {
            return null;
        }
        switch (data?.assignableDashboards?.emptyReason) {
            case CatalogueEmptyReason.NoAccountInPerimeter:
                return {
                    title: c('Grant dashboard').t`This account is not one you carry.`,
                    guidance: c('Grant dashboard')
                        .t`The catalogue came back bounded by a perimeter that does not hold this account, so there is nothing to grant from here.`,
                };
            default:
                return {
                    title: c('Grant dashboard').t`No dashboard is declared on this account.`,
                    guidance: c('Grant dashboard')
                        .t`The catalogue is a whitelist entered by hand, never a mirror of Metabase: an account carries nothing until somebody declares it, so there is nothing to retry here. Declare an entry from the users list filtered on this account, then come back.`,
                };
        }
    };

    const empty = emptiness();

    const submit = () => {
        // The refusal is read off `error` below; without this every refusal also
        // reaches the console as an unhandled rejection.
        grant({ variables: { dashboardId: chosenId as string, membershipId } }).catch(() => {});
    };

    /**
     * What the membership holds now, read from the payload rather than assumed: the
     * one place the blocked state of a brand-new habilitation can be told.
     */
    const done = granted?.grantDashboard;
    const written = done?.find((entry) => entry.dashboardId === chosenId);
    const blocked = written ? blockedNotice(written.blockedReason) : null;

    return (
        <Modal open onClose={onClose} title={c('Grant dashboard').t`Grant a dashboard`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">
                        <b>{personName}</b>
                        {/* The account, always named: the habilitation is granted under
                            it, and the person may be attached to several. */}
                        <span className="text-text-600"> · {organizationName}</span>
                    </p>

                    {done ? (
                        <Alert
                            type={blocked ? 'warning' : 'success'}
                            title={
                                blocked
                                    ? c('Grant dashboard').t`Granted, and it opens nothing today.`
                                    : c('Grant dashboard').t`The dashboard is granted.`
                            }
                        >
                            <p className="text-sm">
                                {blocked
                                    ? blocked.sentence
                                    : c('Grant dashboard')
                                          .t`It is held under this account, and it is under this account that it will be read — the habilitation hangs from the membership, not from the person.`}
                            </p>
                            {/* The fourth condition, which is not a reason of the
                                dashboard: the token maker requires an active
                                membership. Said here too, because the operator has
                                just granted something and is entitled to know it
                                waits. */}
                            {inactiveAccess && <p className="mt-2 text-sm">{inactiveAccess}</p>}
                        </Alert>
                    ) : (
                        <>
                            {catalogueLoading && (
                                <p className="text-xs text-text-600">{c('Grant dashboard')
                                    .t`Reading this account's catalogue…`}</p>
                            )}

                            {catalogueError && <RefusalNotice refusal={readRefusal()} />}

                            {empty && (
                                <Alert type="info" title={empty.title}>
                                    <p className="text-sm">{empty.guidance}</p>
                                </Alert>
                            )}

                            {single && (
                                <div>
                                    <p className="text-sm text-text-900">
                                        {c('Grant dashboard')
                                            .t`One entry is declared on this account, so there is nothing to pick:`}{' '}
                                        <b>{single.name}</b>
                                    </p>
                                    {chosenBlocker && (
                                        <p className="mt-1.5 text-sm text-warning-600">
                                            {c('Grant dashboard')
                                                .t`It cannot be granted here — ${chosenBlocker}. The catalogue of this account is where that is settled.`}
                                        </p>
                                    )}
                                </div>
                            )}

                            {!single && options.length > 0 && (
                                <div>
                                    <label
                                        htmlFor="grant-dashboard-entry"
                                        className="mb-1.5 block text-sm font-medium text-text-900"
                                    >
                                        {c('Grant dashboard').t`Entry of this account's catalogue`}
                                    </label>
                                    <Select
                                        id="grant-dashboard-entry"
                                        options={options}
                                        value={options.find((option) => option.value === selectedId)}
                                        onChange={(option) => setSelectedId(option.value)}
                                        placeholder={c('Grant dashboard').t`Pick an entry`}
                                    />
                                </div>
                            )}

                            {/* Before the click, because it changes what granting means
                                right now: the habilitation will be written and will wait. */}
                            {inactiveAccess && !empty && !catalogueError && (
                                <p className="text-xs text-text-600">{inactiveAccess}</p>
                            )}

                            {error && <RefusalNotice refusal={dashboardRefusalOf(error, 'grant')} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                {done ? (
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Done`}
                    </Button>
                ) : (
                    <>
                        <Button category="weak" onClick={onClose}>
                            {c('Action').t`Cancel`}
                        </Button>
                        <Button
                            category="default"
                            loading={loading}
                            disabled={!chosenId || Boolean(chosenBlocker)}
                            title={chosenBlocker ?? undefined}
                            onClick={submit}
                        >
                            {c('Grant dashboard').t`Grant`}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default GrantDashboardDialog;
